// The console's own building blocks, composed from the kit (@clavian/ui-web) — the pieces every list,
// dialog and settings page shares so no screen hand-rolls its frame (BRAND.md §6.1, apps/console/DESIGN.md).
//
// Each of these is a candidate to move into the kit once a second app needs it (the PR lists them under
// "Promote to kit"): the page frame, the list toolbar and its search field, row actions, the setting
// row, the section card, the inline notice, a facts list and the loading skeletons.

import { useTranslation } from '@clavian/i18n/react';
import { Button, cx, Dialog, EmptyState, InfoTip, Menu, PageHeader, PageHero, SegmentedControl, Skeleton, type DialogProps, type MenuEntry } from '@clavian/ui-web';
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';

import { Icon, SearchIcon } from '../shell/nav-icons';

// -------------------------------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------------------------------

export interface PageProps {
  readonly title: ReactNode;
  /** One line of purpose. */
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  /** At most one primary action (spec/13 §2). */
  readonly actions?: ReactNode;
  readonly meta?: ReactNode;
  /** Small print as an info tip beside the title. */
  readonly tip?: ReactNode;
  /** Open with a hero (an overview page) instead of the flat header. */
  readonly hero?: PageHeroOptions;
  /** `wide` lets a live canvas (Floor, Kitchen, Sell) use the whole width. */
  readonly width?: 'default' | 'wide' | 'narrow';
  readonly className?: string;
  readonly children?: ReactNode;
}

/** What turns a page's header into a hero (spec/13 §2): an overview page, not a working one. */
export interface PageHeroOptions {
  /** The name the tile's initials are drawn from; omit for no tile. */
  readonly tileName?: string;
  /** A StatusPill or Badge beside the title. */
  readonly status?: ReactNode;
  /** Three or four `HeroStat`, only from data the screen already loads. */
  readonly stats?: ReactNode;
}

/**
 * A page: its header (title, one line of purpose, at most one primary action), then its body. An overview page passes
 * `hero` and opens with the kit's PageHero instead; a working page keeps the flat header. `tip` is the small print
 * (how a feature works, what is audited) as an info tip beside the title, so the body starts with the work.
 */
export function Page({ title, description, eyebrow, actions, meta, tip, hero, width = 'default', className, children }: PageProps): ReactNode {
  const beside =
    meta === undefined && tip === undefined ? undefined : (
      <>
        {meta}
        {tip === undefined ? null : <InfoTip>{tip}</InfoTip>}
      </>
    );
  return (
    <div className={cx('console-page', width !== 'default' && `console-page--${width}`, className)}>
      {hero === undefined ? (
        <PageHeader title={title} description={description} eyebrow={eyebrow} actions={actions} meta={beside} />
      ) : (
        <PageHero
          title={title}
          breadcrumb={eyebrow}
          meta={description}
          actions={actions}
          {...(hero.tileName === undefined ? {} : { tileName: hero.tileName })}
          status={
            hero.status === undefined && beside === undefined ? undefined : (
              <>
                {hero.status}
                {beside}
              </>
            )
          }
          stats={hero.stats}
        />
      )}
      {children}
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Toolbar
// -------------------------------------------------------------------------------------------------

/** The row between a page header and its list: search, filters, a view toggle. */
export function Toolbar({ children, className, label }: { readonly children: ReactNode; readonly className?: string; readonly label?: string }): ReactNode {
  return (
    <div className={cx('console-toolbar', className)} role={label === undefined ? undefined : 'toolbar'} aria-label={label}>
      {children}
    </div>
  );
}

/** Pushes what follows it in a toolbar to the trailing edge. */
export function ToolbarSpring(): ReactNode {
  return <span className="console-toolbar__spring" aria-hidden="true" />;
}

export interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  /** The accessible name; shown as the placeholder when none is given. */
  readonly label: string;
  readonly className?: string;
}

/** A search box with its glyph. The label names it for a screen reader; the placeholder shows it. */
export function SearchField({ label, placeholder, className, ...rest }: SearchFieldProps): ReactNode {
  return (
    <span className={cx('console-search', className)}>
      <span className="console-search__icon" aria-hidden="true">
        <SearchIcon />
      </span>
      <input {...rest} type="search" className="clav-input console-search__input" aria-label={label} placeholder={placeholder ?? label} autoComplete="off" spellCheck={false} />
    </span>
  );
}

export type ViewMode = 'grid' | 'list';

/** Cards or a table — the list pages' view toggle. */
export function ViewToggle({ value, onValueChange }: { readonly value: ViewMode; readonly onValueChange: (value: ViewMode) => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <SegmentedControl<ViewMode>
      size="sm"
      label={t('common.view.label')}
      value={value}
      onValueChange={onValueChange}
      options={[
        { value: 'grid', label: t('common.view.grid'), icon: <Icon name="grid" /> },
        { value: 'list', label: t('common.view.list'), icon: <Icon name="list" /> },
      ]}
    />
  );
}

// -------------------------------------------------------------------------------------------------
// Row actions
// -------------------------------------------------------------------------------------------------

/** A row's actions, behind one "more" button named for the row. */
export function RowActions({ name, items, align = 'end' }: { readonly name: string; readonly items: readonly MenuEntry[]; readonly align?: 'start' | 'end' }): ReactNode {
  const { t } = useTranslation('console');
  if (items.length === 0) return null;
  return (
    <Menu
      align={align}
      items={items}
      trigger={<Button variant="ghost" size="sm" className="console-row-actions" icon={<Icon name="more" />} label={t('common.actionsFor', { name })} />}
    />
  );
}

// -------------------------------------------------------------------------------------------------
// Sections and settings rows
// -------------------------------------------------------------------------------------------------

export interface SectionProps {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Small print as an info tip beside the title (how it works, what is recorded). */
  readonly tip?: ReactNode;
  readonly actions?: ReactNode;
  readonly titleAs?: 'h2' | 'h3';
  /**
   * `padded` (the default) for anything that is not a list: facts, prose, a form, a notice.
   * `rows` for a table or a list whose rows pad themselves: it runs edge to edge, and anything else
   * in the body — an empty line, a skeleton, an error, a "more" button — is padded for it, so no
   * content can ever sit on the card's border (the role sheet once did).
   */
  readonly body?: 'padded' | 'rows';
  readonly className?: string;
  readonly id?: string;
  readonly children?: ReactNode;
}

/** A grouped card with a clear header: settings groups, a detail's blocks, a report's panels. */
export function Section({ title, description, tip, actions, titleAs: Heading = 'h2', body = 'padded', className, id, children }: SectionProps): ReactNode {
  const headingId = useId();
  return (
    <section className={cx('console-section', body === 'rows' && 'console-section--rows', className)} aria-labelledby={title === undefined ? undefined : headingId} id={id}>
      {title !== undefined || actions !== undefined ? (
        <header className="console-section__head">
          <div className="console-section__heading">
            {title !== undefined ? (
              <div className="console-section__titlerow">
                <Heading id={headingId} className="console-section__title">
                  {title}
                </Heading>
                {tip === undefined ? null : <InfoTip>{tip}</InfoTip>}
              </div>
            ) : null}
            {description !== undefined ? <p className="console-section__description">{description}</p> : null}
          </div>
          {actions !== undefined ? <div className="console-section__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="console-section__body">{children}</div>
    </section>
  );
}

export interface SettingRowProps {
  readonly label: ReactNode;
  readonly description?: ReactNode;
  /** Beside the label: a source badge, a lock. */
  readonly meta?: ReactNode;
  readonly control: ReactNode;
  /** `stack` puts a wide control (a list, the hours) under the text rather than beside it. */
  readonly layout?: 'inline' | 'stack';
  readonly className?: string;
}

/** One setting as a row: the label and what it does on the left, the control on the right. */
export function SettingRow({ label, description, meta, control, layout = 'inline', className }: SettingRowProps): ReactNode {
  return (
    <div className={cx('console-setting-row', layout === 'stack' && 'console-setting-row--stack', className)}>
      <div className="console-setting-row__text">
        <p className="console-setting-row__label">
          {label}
          {meta}
        </p>
        {description !== undefined ? <p className="console-setting-row__description">{description}</p> : null}
      </div>
      <div className="console-setting-row__control">{control}</div>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Edit in place
// -------------------------------------------------------------------------------------------------

export interface InlineEditProps {
  /** What the value is; names the input and the edit button for a screen reader. */
  readonly label: string;
  /** The value as it reads at rest: "30 min", "₹500.00", "No limit". */
  readonly display: ReactNode;
  /** The value as the input starts: "30", "500.00", "" for none. */
  readonly draft: string;
  readonly placeholder?: string;
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  /** Checks and saves the text. Resolves to an error message to show, or `undefined` once saved. */
  readonly onSave: (text: string) => Promise<string | undefined>;
  readonly disabled?: boolean;
}

/**
 * A value you change where you read it (BRAND.md §6.1, "product, not form entry"): at rest it is the
 * value and a quiet pencil; a click turns it into a field with Save and Cancel. Enter saves, Escape
 * cancels, and the value is only shown as changed once the server has said so — a limit on money is
 * not optimistic.
 */
export function InlineEdit({ label, display, draft, placeholder, inputMode, onSave, disabled = false }: InlineEditProps): ReactNode {
  const { t } = useTranslation('console');
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const errorId = useId();

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  function open(): void {
    setText(draft);
    setError(undefined);
    setEditing(true);
  }

  function close(): void {
    setEditing(false);
    setError(undefined);
    // Give focus back to where it came from, so a keyboard user keeps their place.
    queueMicrotask(() => trigger.current?.focus());
  }

  async function save(event?: FormEvent<HTMLFormElement>): Promise<void> {
    event?.preventDefault();
    setPending(true);
    const problem = await onSave(text).finally(() => setPending(false));
    if (problem === undefined) close();
    else setError(problem);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  }

  if (!editing) {
    return (
      <button ref={trigger} type="button" className="console-inline-edit" onClick={open} disabled={disabled} aria-label={t('common.editField', { label })}>
        <span className="console-inline-edit__value">{display}</span>
        {disabled ? null : (
          <span className="console-inline-edit__pencil" aria-hidden="true">
            <Icon name="pencil" />
          </span>
        )}
      </button>
    );
  }

  return (
    // `data-owns-escape`: Escape cancels this edit, and the sheet or dialog around it stands down (the kit's
    // `ownsEscape`; Radix hears Escape first, in the capture phase, so stopPropagation alone cannot).
    <form className="console-inline-edit__form" data-owns-escape onSubmit={(event) => void save(event)} noValidate>
      <input
        ref={input}
        className="clav-input clav-input--numeric console-inline-edit__input"
        aria-label={label}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={error === undefined ? undefined : errorId}
        value={text}
        placeholder={placeholder}
        inputMode={inputMode}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
        disabled={pending}
      />
      <Button type="submit" size="sm" variant="primary" busy={pending} icon={<Icon name="check" />} label={pending ? t('common.saving') : t('common.save')} />
      <Button type="button" size="sm" variant="ghost" icon={<Icon name="close" />} label={t('common.cancel')} onClick={close} disabled={pending} />
      {error === undefined ? null : (
        <p id={errorId} className="console-inline-edit__error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// Notices, facts, empty and loading
// -------------------------------------------------------------------------------------------------

export type NoticeTone = 'info' | 'ok' | 'warn' | 'danger';

const NOTICE_ICON: Readonly<Record<NoticeTone, string>> = { info: 'info', ok: 'check', warn: 'alert', danger: 'alert' };

/** An inline message with its tone in words and an icon, never colour alone. */
export function Notice({
  tone = 'info',
  title,
  children,
  action,
  role,
  className,
}: {
  readonly tone?: NoticeTone;
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
  /** `alert` for an error the operator must see now; `status` for a result; none for a standing note. */
  readonly role?: 'alert' | 'status' | 'note';
  readonly className?: string;
}): ReactNode {
  return (
    <div className={cx('console-notice', `console-notice--${tone}`, className)} role={role}>
      <span className="console-notice__icon" aria-hidden="true">
        <Icon name={NOTICE_ICON[tone]} />
      </span>
      <div className="console-notice__body">
        {title !== undefined ? <p className="console-notice__title">{title}</p> : null}
        {children}
      </div>
      {action !== undefined ? <div className="console-notice__action">{action}</div> : null}
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// A dialog that will not throw away one-time content on a stray dismiss
// -------------------------------------------------------------------------------------------------

/** The words the dismiss confirm shows while a dialog holds content that cannot be shown again. */
export interface DismissGuardWords {
  readonly title: ReactNode;
  readonly body: ReactNode;
  /** Keeps the dialog open — the safe default, which takes focus. */
  readonly keep: string;
  /** Closes anyway, losing the content. */
  readonly discard: string;
}

/**
 * The close logic for a dialog that, once it is showing something the server will not repeat — a batch
 * of coupon codes, a set of backup codes — must not let an accidental Escape or a click on the scrim
 * throw it away. While `guarded`, a dismiss gesture asks first; the dialog's own Done still closes it.
 * The Dialog is controlled, so vetoing a close simply keeps `open` true and the content on screen.
 */
export function useDismissGuard(open: boolean, close: () => void) {
  const [guarded, setGuarded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Closed from the outside (Done, or the parent): forget everything, so the next open starts clean.
  // Adjusted during render on the prop change (React's documented "store previous render" pattern),
  // not in an effect; setting a value to what it already is bails out, so this never loops.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) {
      setGuarded(false);
      setConfirming(false);
    }
  }
  const onOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      if (guarded) {
        setConfirming(true);
        return;
      }
      close();
    },
    [guarded, close],
  );
  const discard = useCallback(() => {
    setConfirming(false);
    setGuarded(false);
    close();
  }, [close]);
  const keep = useCallback(() => setConfirming(false), []);
  return { onOpenChange, confirming, guarded, setGuarded, keep, discard } as const;
}

/**
 * The confirm itself, shown in place above the dialog's content — not as a second dialog on top — so the content
 * can still be copied first.
 */
function DismissGuard({ words, onKeep, onDiscard }: { readonly words: DismissGuardWords; readonly onKeep: () => void; readonly onDiscard: () => void }): ReactNode {
  const keepRef = useRef<HTMLButtonElement>(null);
  // The safe choice takes focus, so the Enter that a keyboard user reaches for keeps the content.
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  return (
    <Notice tone="warn" role="alert" title={words.title} className="console-dismiss-guard">
      <p>{words.body}</p>
      <div className="console-form__actions console-form__actions--end">
        <Button ref={keepRef} variant="secondary" onClick={onKeep}>
          {words.keep}
        </Button>
        <Button variant="ghost" onClick={onDiscard}>
          {words.discard}
        </Button>
      </div>
    </Notice>
  );
}

export interface GuardedDialogProps extends Omit<DialogProps, 'onOpenChange' | 'children' | 'dirty' | 'discardWords'> {
  /** Close for real (what Done and an unguarded dismiss call). */
  readonly onClose: () => void;
  readonly guard: DismissGuardWords;
  /** The dialog's body. The callback marks the dialog as holding one-time content (or no longer). */
  readonly children: (setGuarded: (on: boolean) => void) => ReactNode;
}

/**
 * A framed `Dialog` that confirms before a stray dismiss discards one-time content. The body calls `setGuarded`
 * to arm the guard the moment it shows what cannot be shown again, and clears it otherwise. Everything
 * else is the kit's Dialog, unchanged.
 */
export function GuardedDialog({ open, onClose, guard, children, size = 'md', ...rest }: GuardedDialogProps): ReactNode {
  const g = useDismissGuard(open ?? false, onClose);
  return (
    <Dialog {...rest} size={size} open={open} onOpenChange={g.onOpenChange}>
      {g.confirming ? <DismissGuard words={guard} onKeep={g.keep} onDiscard={g.discard} /> : null}
      {children(g.setGuarded)}
    </Dialog>
  );
}

// -------------------------------------------------------------------------------------------------
// A step shown in place inside a dialog, rather than a dialog on a dialog
// -------------------------------------------------------------------------------------------------

/** A step's first field — a text box, a select, a combobox — is where its focus goes; any control if it has none. */
const FIELD = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled])';
const FOCUSABLE = `${FIELD}, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])`;

/**
 * Focus for a step a dialog shows in place — a form that would otherwise open a second dialog on top (recording an
 * ID check inside the player, a wallet top-up on the Wallet tab). When the step opens, focus moves to its first
 * control; when it closes, back to the button that opened it. Put `stepRef` on the step's wrapping `div` and spread
 * `opener` on the button (it may unmount while the step is open: it is found again, by a data attribute, once it is
 * back — so a step with several openers spreads `opener` on the one that was pressed).
 */
export function useStepFocus(open: boolean) {
  const id = useId();
  const stepRef = useRef<HTMLDivElement | null>(null);
  const was = useRef(open);
  useEffect(() => {
    const step = stepRef.current;
    if (open && !was.current) (step?.querySelector<HTMLElement>(FIELD) ?? step?.querySelector<HTMLElement>(FOCUSABLE))?.focus();
    // React's ids hold no quote or backslash, so the quoted attribute value needs no escaping.
    if (!open && was.current) document.querySelector<HTMLElement>(`[data-step-opener="${id}"]`)?.focus();
    was.current = open;
  }, [open, id]);
  return { stepRef, opener: { 'data-step-opener': id } } as const;
}

/** A definition list of short facts, label over value, in a responsive grid. */
export function Facts({ items, columns = 2, className }: { readonly items: readonly { readonly label: ReactNode; readonly value: ReactNode; readonly key?: string }[]; readonly columns?: 1 | 2 | 3; readonly className?: string }): ReactNode {
  return (
    <dl className={cx('console-facts', `console-facts--${columns}`, className)}>
      {items.map((item, index) => (
        <div className="console-facts__item" key={item.key ?? index}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A role that may not see a screen: said plainly, in the page's place. */
export function Forbidden({ message }: { readonly message: ReactNode }): ReactNode {
  const { t } = useTranslation('console');
  return <EmptyState icon={<Icon name="lock" />} title={t('common.forbiddenTitle')} description={message} />;
}

/** A table's rows while it loads: the final layout, greyed. */
export function TableSkeleton({ rows = 6, label }: { readonly rows?: number; readonly label?: string }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-skeleton-table" aria-busy="true" role="status" aria-label={label ?? t('app.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <div className="console-skeleton-table__row" key={index}>
          <Skeleton shape="text" width="18%" />
          <Skeleton shape="text" width="32%" />
          <Skeleton shape="text" width="14%" />
          <Skeleton shape="text" width="10%" />
        </div>
      ))}
    </div>
  );
}

/** A grid of cards while it loads. */
export function CardsSkeleton({ count = 6, height = '7.5rem', label }: { readonly count?: number; readonly height?: string; readonly label?: string }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-skeleton-cards" aria-busy="true" role="status" aria-label={label ?? t('app.loading')}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} height={height} />
      ))}
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// The summary strip and state rails (ADR-0043)
// -------------------------------------------------------------------------------------------------

/**
 * A screen's headline figures as one summary strip (the Floor's, shared since the 2026-10-07 rollout): the kit's
 * StatCards as cells of a single surface split by hairlines. A cell takes a rail with `console-summary__cell--live`
 * (what is running now) or `console-summary__cell--ok`, or the StatCard's own `warn` or `danger` tone; a meter under a
 * figure is a `SummaryMeter` in its hint.
 */
export function SummaryStrip({ label, children, className }: { readonly label?: string; readonly children: ReactNode; readonly className?: string }): ReactNode {
  return (
    <div className={cx('console-summary', className)} role={label === undefined ? undefined : 'group'} aria-label={label}>
      {children}
    </div>
  );
}

/** How full something is (0 to 1), as a thin meter in the brand gradient under a strip's figure. Decoration: the words say it too. */
export function SummaryMeter({ fill }: { readonly fill: number }): ReactNode {
  const clamped = Number.isFinite(fill) ? Math.min(1, Math.max(0, fill)) : 0;
  return <span className="console-summary__meter" style={{ ['--fill' as string]: clamped }} aria-hidden="true" />;
}

/** The state a card or a row shows by its rail (`.console-rail` with `data-rail`, styles/ui.css): the tile's language. */
export type RailTone = 'live' | 'ok' | 'warn' | 'danger' | 'quiet' | 'off' | 'class';

/** A small status word with a dot: a health, a paired state. Colour never alone. */
export function Dot({ tone, children }: { readonly tone: 'ok' | 'warn' | 'danger' | 'neutral' | 'session'; readonly children: ReactNode }): ReactNode {
  return (
    <span className={cx('console-dot', `console-dot--${tone}`)}>
      <span className="console-dot__mark" aria-hidden="true" />
      {children}
    </span>
  );
}
